/* Collection-wide availability ordering, using live Shopify Liquid data.
   The lightweight index is fetched first; only the required card pages follow.
   Native filters and the customer's chosen sort stay intact within each group.
   With JS disabled / a failed request, the original Shopify page is retained. */
(function () {
  'use strict';
  function inStockFirst(items, available) {
    return items.filter(available).concat(items.filter(function (item) { return !available(item); }));
  }
  function pageSize(fallback) {
    var requested = Number(new URL(location.href).searchParams.get('per_page'));
    return requested === 24 || requested === 48 ? requested : fallback;
  }
  function currentPage() {
    var value = Number(new URL(location.href).searchParams.get('page'));
    return Number.isInteger(value) && value > 0 ? value : 1;
  }
  function pageUrl(page) {
    var url = new URL(location.href);
    url.searchParams.set('page', page);
    url.hash = 'productGrid';
    return url.pathname + url.search + url.hash;
  }
  function navigation(grid, total, size, current) {
    var area = grid.parentElement;
    var nav = area.querySelector('#collectionPagination') || area.querySelector('.pagination');
    if (!nav) { nav = document.createElement('nav'); grid.after(nav); }
    nav.id = 'collectionPagination';
    nav.className = 'pagination';
    nav.setAttribute('aria-label', 'Product pages');
    nav.replaceChildren();
    var pages = Math.max(1, Math.ceil(total / size));
    nav.style.display = pages > 1 ? '' : 'none';
    nav.toggleAttribute('data-has-pages', pages > 1);
    function link(page, label) {
      var el = document.createElement(page === current ? 'span' : 'a');
      el.className = 'page-btn' + (page === current ? ' current' : '');
      el.textContent = label || String(page);
      if (page === current) el.setAttribute('aria-current', 'page');
      else el.href = pageUrl(page);
      nav.appendChild(el);
    }
    if (current > 1) link(current - 1, 'Previous');
    var last = 0;
    for (var page = 1; page <= pages; page++) {
      if (page !== 1 && page !== pages && Math.abs(page - current) > 2) continue;
      if (last && page > last + 1) {
        var dots = document.createElement('span'); dots.textContent = '…'; nav.appendChild(dots);
      }
      link(page); last = page;
    }
    if (current < pages) link(current + 1, 'Next');
  }
  function updateCounts(total) {
    document.querySelectorAll('[data-product-count-role], #collectionCount').forEach(function (el) {
      var noun = el.dataset.productCountRole === 'hero' ? 'item' : 'product';
      el.dataset.liquidCount = total;
      el.textContent = total + ' ' + noun + (total === 1 ? '' : 's');
    });
  }
  function initialiseCards() {
    if (window.jdgm && typeof window.jdgm.init === 'function') {
      try { window.jdgm.init(); } catch (error) { /* reviews must not block shopping */ }
    }
    document.dispatchEvent(new CustomEvent('oikos:products-updated'));
  }
  function paginateExisting(grid) {
    var cards = inStockFirst(Array.from(grid.querySelectorAll('.product-card')), function (card) {
      return card.dataset.soldout !== '1';
    });
    var size = pageSize(48);
    var page = Math.min(currentPage(), Math.max(1, Math.ceil(cards.length / size)));
    if (cards.length) grid.replaceChildren.apply(grid, cards.slice((page - 1) * size, page * size));
    navigation(grid, cards.length, size, page);
    updateCounts(cards.length);
  }
  async function sectionDocument(section, page) {
    var url = new URL(location.href);
    url.hash = '';
    url.searchParams.delete('sections');
    url.searchParams.set('section_id', section);
    url.searchParams.set('page', page);
    var controller = new AbortController();
    var timeout = setTimeout(function () { controller.abort(); }, 20000);
    try {
      var response = await fetch(url.pathname + url.search, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw Error('Could not load product page');
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    } finally { clearTimeout(timeout); }
  }
  async function indexPage(page) {
    var doc = await sectionDocument('stock-order-index', page);
    var payload = doc.querySelector('[data-stock-index]');
    if (!payload) throw Error('Availability index missing');
    var data = JSON.parse(payload.textContent);
    if (data.page !== page || !Array.isArray(data.items) || data.total > 25000) throw Error('Invalid availability index');
    return data;
  }
  async function mapLimited(values, action) {
    var next = 0;
    var result = new Array(values.length);
    async function worker() {
      while (next < values.length) { var i = next++; result[i] = await action(values[i]); }
    }
    await Promise.all(Array.from({ length: Math.min(3, values.length) }, worker));
    return result;
  }
  async function loadCollection(grid) {
    if (window._oikosCustomColourResults) return;
    var original = Array.from(grid.childNodes);
    var sourceSize = Number(grid.dataset.stockSourceSize) || 48;
    var size = pageSize(sourceSize === 250 ? 48 : sourceSize);
    var sizeControl = document.querySelector('[aria-label="Products per page"]');
    if (sizeControl) sizeControl.value = String(size);
    var cache = new Map();
    function collect(root) {
      root.querySelectorAll('.product-card[data-product-id]').forEach(function (card) { cache.set(card.dataset.productId, card); });
    }
    collect(grid);
    var status = document.createElement('p');
    status.setAttribute('role', 'status');
    status.style.cssText = 'padding:20px 0;color:var(--text2);';
    status.textContent = 'Loading products…';
    grid.before(status);
    grid.style.display = 'none';
    grid.setAttribute('aria-busy', 'true');
    var originalNav = grid.parentElement.querySelector('.pagination');
    var navDisplay = originalNav ? originalNav.style.display : '';
    if (originalNav) originalNav.style.display = 'none';
    try {
      var first = await indexPage(1);
      var remaining = Array.from({ length: Math.max(0, first.pages - 1) }, function (_, i) { return i + 2; });
      var batches = await mapLimited(remaining, indexPage);
      var all = first.items.concat.apply(first.items, batches.map(function (batch) { return batch.items; }));
      // Reject truncated/changing responses instead of silently dropping products.
      if (all.length !== first.total || new Set(all.map(function (item) { return item.id; })).size !== all.length) throw Error('Collection changed while loading');
      var keyword = (grid.dataset.stockKeyword || '').toLowerCase();
      all = all.filter(function (item) {
        return (grid.dataset.stockSale !== 'true' || item.sale) && (!keyword || item.title.toLowerCase().includes(keyword));
      });
      all = inStockFirst(all, function (item) { return item.available; });
      var page = Math.min(currentPage(), Math.max(1, Math.ceil(all.length / size)));
      var wanted = all.slice((page - 1) * size, page * size);
      var sourcePages = Array.from(new Set(wanted.filter(function (item) { return !cache.has(item.id); }).map(function (item) {
        return Math.floor(item.position / sourceSize) + 1;
      })));
      await mapLimited(sourcePages, async function (number) {
        var doc = await sectionDocument(grid.dataset.stockSection, number);
        var fetchedGrid = doc.querySelector('#productGrid');
        if (!fetchedGrid || Number(fetchedGrid.dataset.stockSourcePage) !== number) throw Error('Product page missing');
        collect(fetchedGrid);
      });
      var selected = wanted.map(function (item) { return cache.get(item.id); });
      if (selected.some(function (card) { return !card; })) throw Error('Product changed while loading');
      grid.replaceChildren.apply(grid, selected);
      if (!selected.length) {
        var empty = document.createElement('p'); empty.textContent = 'No products found.'; grid.appendChild(empty);
      }
      navigation(grid, all.length, size, page);
      updateCounts(all.length);
      status.remove();
      initialiseCards();
    } catch (error) {
      grid.replaceChildren.apply(grid, original);
      if (originalNav) originalNav.style.display = navDisplay;
      status.textContent = 'Showing the original product order. ';
      var retry = document.createElement('button');
      retry.type = 'button'; retry.className = 'page-btn'; retry.textContent = 'Retry in-stock first';
      retry.addEventListener('click', function () { status.remove(); loadCollection(grid); });
      status.appendChild(retry);
      console.warn('In-stock ordering could not finish.', error);
    } finally {
      grid.style.display = '';
      grid.removeAttribute('aria-busy');
    }
  }
  window.OikosStockOrder = { inStockFirst: inStockFirst, paginateExisting: paginateExisting };
  function start() {
    var grid = document.querySelector('#productGrid[data-stock-section]');
    if (grid) loadCollection(grid);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
